/**
 * @module app.core
 */

import { autoinject, bindingMode, TaskQueue } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { IUserSearchFilters } from 'app/core/services/user-service';
import { ICollapse } from 'app/shared/attributes/ess-collapse-attributes';
import { fireEvent } from 'app/shared/lib/fire-events';
import { Toaster } from 'app/shared/lib/toaster';
import { countUserFilters } from 'app/core/services/user-service';
import { guid } from 'app/shared/lib/guid';
import { EventAggregator } from 'aurelia-event-aggregator';
import { SetFocusEvent } from 'app/shared/lib/events';
import { isNullOrUndefined } from 'app/shared/lib/utilities';

interface IUserSearchCustomEvent { userSearchFilters: IUserSearchFilters }
export type UserSearchCustomEvent = CustomEvent<IUserSearchCustomEvent>;

@autoinject()
export class UserSearchFormCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public userSearchFilters: IUserSearchFilters = {};

    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    public tempUserSearchFilters: IUserSearchFilters = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultCollapsed: boolean = false;

    @bindable.boolean({ defaultBindingMode: bindingMode.oneTime })
    public defaultSearchCriteria: boolean = false;

    protected id: guid = guid();
    protected userAvailableFilters = [
        'active', 'login.loginName', 'application.description', 'login.lastLogInDateTimestamp', 'name.first', 'name.last', 'emailAddress',
        'title', 'department'
    ];

    ////****************** Element References ***************************************////
    protected $collapsible!: ICollapse;
    protected $searchCriteriaHeader!: HTMLElement;
    protected $currentCriteriaHeader!: HTMLElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly $element: Element,
        private readonly taskQueue: TaskQueue,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        if(this.defaultCollapsed) { return; }

        this.taskQueue.queueTask(() => {
            if(this.defaultSearchCriteria) {
                this.showSelectedCriteria();
            } else {
                this.showAvailableCriteria();
            }
        });
    }


    ////****************** Events ***************************************************////
    protected handleSubmit(): void {
        if(!this.state.saving && !this.state.loading) {
            this.fireSearch();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    ////****************** Functions ************************************************////
    public showAvailableCriteria(): void {
        this.$collapsible.open(0);
        this.$collapsible.close(1);
    }

    public showSelectedCriteria(): void {
        this.$collapsible.close(0);
        this.$collapsible.open(1);
    }

    protected fireSearch(): void {
        //check in advance b/c fireEvent is async and would just let showSelectedCriteria run no matter what
        const userFilterCount = countUserFilters(this.tempUserSearchFilters);
        if(userFilterCount === 0) {
            this.logger.warn('Invalid or missing search criteria');
            this.toaster.standard.searchNoCriteria();
            return;
        }

        if(isNullOrUndefined(this.tempUserSearchFilters.loginBetweenEnd)) {
            this.tempUserSearchFilters.loginBetweenEnd = this.tempUserSearchFilters.loginBetweenStart;
        }

        const detail: IUserSearchCustomEvent = {
            userSearchFilters: this.tempUserSearchFilters
        }
        fireEvent(this.$element, 'search', detail);
        this.showSelectedCriteria();
        this.events.publish(new SetFocusEvent(this.$currentCriteriaHeader));
    }

    public clear(returnFocus?: boolean): void {
        this.userSearchFilters = {};
        this.tempUserSearchFilters = { active: true };
        this.showAvailableCriteria();
        fireEvent(this.$element, 'clear-results');

        if(returnFocus) this.events.publish(new SetFocusEvent(this.$searchCriteriaHeader));
    }
}
